import React, { useEffect, useState, useCallback } from 'react';

import JSZip from 'jszip';
import moment from 'moment';

import { useMediaQuery } from '@material-ui/core';

import { useGlobal } from '../../../contexts/Global/context';

import Icon from '../../../components/Icons';
import Button from '../../../components/Button';
import Input from '../../../components/Inputs/Input';
import Select from '../../../components/Inputs/Select';
import DatepickerMui from '../../../components/Inputs/DatepickerMui';
import PageLoader from '../../../components/PageLoader';
import DataTableMui from '../../../components/DataTableMui';
import Dropdown from '../../../components/Dropdown';
import Modal from '../../../components/Modal';
import Radio from '../../../components/Inputs/Radio';
import CustomStatusTag from './CustomStatusTag';
import CartaDeCredito from './CartaDeCredito';
import filterSchema from './schema';

import {
    buscaFaturas,
    linkNfe,
    relatoriosDoKit,
    boletoDaFatura,
    bucarDadosCartaDeCredito,
    getEnderecoCobranca,
    solicitarEKit,
    downloadEKit,
} from '../../../services';

import {
    cloneObject,
    downloadFile,
    generatePdf,
    generatePdfBase64,
    translateContractsGroupsListToOptions,
} from '../../../utils';

import * as S from './styles';

function ConsultarFaturas() {
    const { dataActiveContract, contractsGroupsList, contrastTheme } = useGlobal();

    const DATE_FORMAT_TOP = 'MM/DD/YYYY hh:mm:ss';
    const DATE_FORMAT = 'DD/MM/YYYY';
    const STATUS_OPTIONS = [
        { codigo: 0, nome: 'Em aberto', statusGroup: [0, 3] },
        { codigo: 1, nome: 'Paga', statusGroup: [1] },
        { codigo: 2, nome: 'Cancelada', statusGroup: [2, 5] },
        { codigo: 4, nome: 'Renegociada', statusGroup: [4] },
        { codigo: 6, nome: 'Baixa parcial', statusGroup: [6] },
    ];
    const ALL_CONTRACT_OPTIONS = translateContractsGroupsListToOptions(contractsGroupsList);
    const ALL_CONTRACT_NUMBERS = ALL_CONTRACT_OPTIONS.map((contract) => contract.codigo);

    const warningModalInitialData = {
        title: '',
        subtitle: '',
        visible: false,
    };

    const matchMediaQuery = useMediaQuery('(max-width: 960px)');

    const [dataWarningModal, setDataWarningModal] = useState(warningModalInitialData);
    const [loading, setLoading] = useState(true);
    const [alreadySearched, setAlreadySearched] = useState(false);
    const [dataTable, setDataTable] = useState([]);
    const [showFilters, setShowFilters] = useState(true);
    const [pageLoading, setPageLoading] = useState(false);
    const [loadingEKit, setLoadingEKit] = useState(false);
    const [enableClearSelection, setEnableClearSelection] = useState(true);

    const [newId, setNewId] = useState(0);

    const [filterValues, setFilterValues] = useState({
        ...cloneObject(filterSchema),
        contracts: {
            ...filterSchema.contracts,
            value: [dataActiveContract.numero],
        },
    });

    const adminGroups =
        contractsGroupsList
            ?.filter((group) =>
                group.contratos?.some(
                    (contract) => contract.contratoResponsavelGrupo && contract.classificacaoContatoRH
                )
            )
            .map((group) => ({
                ...group,
                codigo: `${group.idGrupoContrato}`,
                nome: `${group.idGrupoContrato} - ${group.grupoContrato}`,
            })) || [];

    async function handleChangeInput(event) {
        const { id, value, ...rest } = event;

        const field = { ...filterValues[id], value, error: '', ...rest };

        const editedForm = { ...filterValues, [id]: field };

        if (id === 'tipoSelecao') {
            editedForm.contracts.required = value === '1';
            editedForm.contractsGroup.required = value === '2';
        }

        setFilterValues(editedForm);
    }

    async function doSearch() {
        const contractsToSearch = filterValues.contracts.value.filter((contractNumber) => contractNumber !== '1');

        const requestsFaturas = contractsToSearch.reduce((requestList, contractNumber) => {
            const statusToSearch = STATUS_OPTIONS.find((status) => status.codigo === +filterValues.status.value)
                ?.statusGroup || [null];

            const competence = filterValues.competence.value;

            statusToSearch.forEach((status) => {
                requestList.push(
                    buscaFaturas({
                        numContrato: contractNumber,
                        competencia: competence ? `${moment(competence).format('YYYY-MM')}-01` : null,
                        numeroNf: filterValues.nf.value,
                        status: status || status === 0 ? status.toString() : null,
                    })
                );
            });
            return requestList;
        }, []);

        const responsesFaturas = await Promise.all(requestsFaturas);
        const contractList = filterValues.contracts.value;

        let rearrangedContractList = [];

        if (filterValues.status.value === '0' || filterValues.status.value === '2') {
            for (let i = 0; i < contractList.length; i += 1) {
                rearrangedContractList.push(contractList[i]);
                rearrangedContractList.push(contractList[i]);
            }
        } else {
            rearrangedContractList = contractList;
        }

        const treatedResponses = responsesFaturas.reduce((listFaturas, actualList, index) => {
            if (Array.isArray(actualList))
                actualList.forEach((fatura) => {
                    listFaturas.push({
                        ...fatura,
                        numContrato: rearrangedContractList[index],
                    });
                });
            return listFaturas;
        }, []);

        setDataTable(treatedResponses);
    }

    async function handleDownloadNfe({ numContrato, numFatura }) {
        setPageLoading(true);

        const { link } = (await linkNfe({ numContrato, numFatura })) || {};

        setPageLoading(false);

        if (link) {
            window.open(link, '_blank');

            return;
        }

        setDataWarningModal({
            title: 'Arquivo não encontrado',
            subtitle: 'A nota fiscal não está disponivel',
            visible: true,
        });
    }

    async function handleDownloadKit({ numFatura }) {
        setPageLoading(true);

        const { nomeArquivo, arquivoBase64 } = await relatoriosDoKit(numFatura, `relatorio_${numFatura}`);

        setPageLoading(false);

        downloadFile(nomeArquivo, `data:application/zip;base64,${arquivoBase64}`);
    }

    async function getInvoiceSlip(invoiceNumber) {
        setPageLoading(true);

        const { arquivo: base64 } = await boletoDaFatura({ numFatura: invoiceNumber });

        setPageLoading(false);

        downloadFile(`Fatura_${invoiceNumber}`, `data:application/pdf;base64,${base64}`);
    }

    async function handleDownloadCartaCredito(row) {
        setPageLoading(true);

        const { numContrato, numCobranca } = row;

        const payload = {
            numContrato,
            numCobranca,
        };

        const response = await bucarDadosCartaDeCredito(payload);

        if (response[0]) {
            const responseEnderecoCobranca = await getEnderecoCobranca(numContrato);

            if (!responseEnderecoCobranca) return;

            generatePdf(
                <CartaDeCredito
                    form={{
                        dataFatura: { ...row },
                        dataCartaCredito: response[0],
                        dataContrato: { ...dataActiveContract, enderecoCobranca: responseEnderecoCobranca },
                    }}
                />,
                `Carta de crédito ${row.numFatura}`
            );

            setPageLoading(false);

            return;
        }

        setDataWarningModal({
            title: 'Arquivo não encontrado',
            subtitle: 'Este arquivo ainda não foi disponibilizado',
            visible: true,
        });

        setPageLoading(false);
    }

    async function handleDownloadEKit(numFatura) {
        setPageLoading(true);
        setLoadingEKit(true);

        const numeroSolicitacao = await solicitarEKit(numFatura);

        if (!numeroSolicitacao) {
            setDataWarningModal({
                title: 'E-kit não encontrado',
                subtitle: 'Fatura não existe ou Contrato não possui parametrização de E-kit.',
                visible: true,
            });

            setPageLoading(false);
            setLoadingEKit(false);

            return;
        }

        const getFile = async () => {
            const file = await downloadEKit(numFatura, numeroSolicitacao);

            if (file[0] && file[0].errorCode === 160) {
                setTimeout(getFile, 1000);
            } else {
                downloadFile(file.nomeArquivo, `data:application/zip;base64,${file.arquivoBase64}`);
                setPageLoading(false);
                setLoadingEKit(false);
            }
        };

        getFile();
    }

    const [columnsHead, setColumnsHead] = useState([
        {
            key: 'dtEmissaoNf',
            selector: (row) => row.dtEmissaoNf,
            name: 'Emissão',
            sortable: true,
            direction: 'left',
            mutation: (value) => (value ? moment(value).format(DATE_FORMAT) : '-'),
            customStyles: () => ({ padding: '0 16px 0 24px', width: '200px' }),
        },
        {
            key: 'numContrato',
            selector: (row) => row.numContrato || '-',
            name: 'Contrato',
            sortable: true,
            direction: 'left',
            customStyles: () => ({ padding: '0 16px 0 24px', width: '200px' }),
            hideColumn: true,
        },
        {
            key: 'numNotaFiscal',
            selector: (row) => row,
            name: 'Nota fiscal',
            sortable: true,
            direction: 'left',
            mutation: ({ numNotaFiscal, numFatura, numContrato }) => (
                <S.CellWithIcon>
                    <span>{numNotaFiscal || '-'}</span>
                    {numNotaFiscal && (
                        <Icon
                            color={contrastTheme ? '#FFF333' : '#EC6E0C'}
                            name="open_in_new"
                            tooltip={<S.TooltipText>Acessar nota fiscal</S.TooltipText>}
                            onClick={() => handleDownloadNfe({ numFatura, numContrato })}
                            size={16}
                        />
                    )}
                </S.CellWithIcon>
            ),
            customStyles: () => ({ width: '200px' }),
        },
        {
            key: 'dtVencimento',
            selector: (row) => (row.dtVencimento ? moment(row.dtVencimento, DATE_FORMAT_TOP).toISOString() : '-'),
            name: 'Vencimento',
            sortable: true,
            direction: 'left',
            mutation: (value) => moment(value).format(DATE_FORMAT),
            customStyles: () => ({ width: 'calc(100% - 700px)', height: '60px' }),
        },
        {
            key: 'valor',
            selector: (row) => row,
            name: 'Valor da fatura',
            sortable: true,
            direction: 'center',
            mutation: (row) => {
                const formattedValue = (+row.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

                return (
                    <S.CellWithIcon>
                        <span>{formattedValue}</span>
                        <Icon
                            name="request_quote"
                            onClick={() => handleDownloadCartaCredito(row)}
                            tooltip={<S.TooltipText>Visualizar carta de crédito</S.TooltipText>}
                            size={16}
                            color={contrastTheme ? '#FFF333' : '#0B8A1C'}
                        />
                    </S.CellWithIcon>
                );
            },
            customStyles: () => ({ width: '200px' }),
        },
        {
            key: 'status',
            selector: (row) => row,
            name: 'Status da fatura',
            sortable: true,
            direction: 'center',
            mutation: ({ status, numFatura }) => (
                <S.CellWithIcon>
                    <CustomStatusTag status={status} />
                    {+status !== 1 && (
                        <Icon
                            name="file_download"
                            color={contrastTheme ? '#FFF333' : '#EC6E0C'}
                            onClick={() => getInvoiceSlip(numFatura)}
                            tooltip={<S.TooltipText>Baixar boleto</S.TooltipText>}
                            size={22}
                        />
                    )}
                </S.CellWithIcon>
            ),
            customStyles: () => ({ width: '173px' }),
            customClassName: 'status-column',
        },
        {
            key: 'eKit',
            selector: (row) => row,
            name: 'E-kit',
            sortable: false,
            direction: 'center',
            mutation: ({ numFatura }) => (
                <S.CellWithIcon>
                    <Icon
                        name="file_download"
                        color={contrastTheme ? '#FFF333' : '#EC6E0C'}
                        onClick={() => handleDownloadEKit(numFatura)}
                        tooltip={<S.TooltipText>Baixar E-kit</S.TooltipText>}
                        size={22}
                    />
                </S.CellWithIcon>
            ),
            customStyles: () => ({ width: '70px' }),
            customClassName: 'status-column',
        },
        {
            key: 'actions',
            selector: (row) => row,
            name: 'Mais',
            sortable: false,
            direction: 'center',
            mutation: ({ numFatura }) => {
                const ComponentIcon = () => (
                    <Icon color={contrastTheme ? '#FFF333' : '#EC6E0C'} name="more_horiz" size={24} />
                );

                const options = [
                    {
                        label: 'Relatórios do kit',
                        onClick: () => handleDownloadKit({ numFatura }),
                    },
                ];

                return <Dropdown style={{ marginLeft: '-95px' }} component={ComponentIcon} options={options} />;
            },
            customStyles: () => ({
                width: '43px',
                textAlign: 'center',
                cursor: 'pointer',
                zIndex: '9999',
                padding: '0',
            }),
        },
    ]);

    function handleColumns() {
        const newColumnsHead = [...columnsHead];

        if (filterValues.tipoSelecao.value === '1' && filterValues.contracts.value.length > 0) {
            const isOneContractSearch = filterValues.contracts.value.length > 1;

            newColumnsHead[0].hideColumn = isOneContractSearch;
            newColumnsHead[1].hideColumn = !isOneContractSearch;

            setColumnsHead([...newColumnsHead]);
        }
        if (filterValues.tipoSelecao.value === '2' && filterValues.contractsGroup?.value?.length >= 1) {
            const isOneContractGroupSearch = filterValues.contractsGroup?.value?.length >= 1;

            newColumnsHead[0].hideColumn = isOneContractGroupSearch;
            newColumnsHead[1].hideColumn = !isOneContractGroupSearch;

            setColumnsHead([...newColumnsHead]);
        }
    }

    async function handleGenerateReport() {
        setLoading(true);

        setNewId(newId + 1);

        let isContractGroup = false;

        if (filterValues.tipoSelecao.value === '2') isContractGroup = true;
        const contractList = isContractGroup ? filterValues.contractsGroup.value : filterValues.contracts.value;

        if (filterValues.tipoSelecao.value === '1') {
            setLoading(true);

            await doSearch();

            handleColumns();

            setLoading(true);
        } else {
            const listRequests = contractList.reduce((requestList, contractNumber) => {
                const competence = filterValues.competence.value;
                const statusToSearch = STATUS_OPTIONS.find((status) => status.codigo === +filterValues.status.value)
                    ?.statusGroup || [null];
                statusToSearch.forEach((status) => {
                    requestList.push(
                        buscaFaturas({
                            numGrupoEmpresa: contractNumber,
                            competencia: competence ? `${moment(competence).format('YYYY-MM')}-01` : null,
                            numeroNf: filterValues.nf.value,
                            status: status || status === 0 ? status.toString() : null,
                        })
                    );
                });
                return requestList;
            }, []);

            const resultList = await Promise.all(listRequests);

            let rearrangedContractList = [];

            if (filterValues.status.value === '0' || filterValues.status.value === '2') {
                for (let i = 0; i < contractList.length; i += 1) {
                    rearrangedContractList.push(contractList[i]);
                    rearrangedContractList.push(contractList[i]);
                }
            } else {
                rearrangedContractList = contractList;
            }

            const treatedResponses = resultList.reduce((listFaturas, actualList) => {
                if (Array.isArray(actualList))
                    actualList.forEach((fatura) => {
                        listFaturas.push({
                            ...fatura,
                            numContrato: fatura.numContrato,
                        });
                    });
                return listFaturas;
            }, []);

            setDataTable(treatedResponses);
            handleColumns();
        }

        setLoading(false);
    }

    function handleClearContracts() {
        if (filterValues.tipoSelecao.value === '1') {
            if (filterValues.contracts?.value?.length > 0) {
                handleChangeInput({ ...filterValues.contracts, value: [] });
                return;
            }
        }
        if (filterValues.contractsGroup?.value?.length > 0) {
            handleChangeInput({ ...filterValues.contractsGroup, value: [] });
        }
    }

    function handleChangeFilter(event) {
        const { id, value, numeroContrato, isChecked } = event;

        let updatedSelectedContracts;
        if (id === 'contracts') {
            if (numeroContrato === '1') {
                if (isChecked) {
                    updatedSelectedContracts = [...ALL_CONTRACT_NUMBERS, '1'];
                } else {
                    updatedSelectedContracts = [];
                }
            } else if (ALL_CONTRACT_NUMBERS.length === value.filter((code) => code !== '1').length) {
                updatedSelectedContracts = [...value, '1'];
            } else if (!isChecked) {
                updatedSelectedContracts = value.filter((code) => code !== '1');
            }
        }

        const field = cloneObject({ ...filterValues[id], value: updatedSelectedContracts || value, error: '' });

        setFilterValues({
            ...filterValues,
            [id]: field,
        });
    }

    useEffect(() => {
        setEnableClearSelection(
            (filterValues.tipoSelecao.value === '1' && filterValues.contracts.value?.length > 0) ||
                (filterValues.tipoSelecao.value === '2' && filterValues.contractsGroup.value?.length > 0)
        );
    }, [filterValues]);

    async function handleSearch() {
        if (filterValues.contracts.value.length > 0) {
            setLoading(true);

            await doSearch();

            handleColumns();

            setLoading(false);

            setAlreadySearched(true);
        }
    }

    async function handleDownloadAll() {
        setPageLoading(true);

        // eslint-disable-next-line consistent-return
        const requests = [...dataTable].map(async (row) => {
            let base64CartaCredito;

            const { numContrato, numCobranca, numFatura } = row;

            const numeroSolicitacao = await solicitarEKit(numFatura);

            const response = (await bucarDadosCartaDeCredito({ numContrato, numCobranca })) || [];

            if (response[0]) {
                const responseEnderecoCobranca = await getEnderecoCobranca(numContrato);

                base64CartaCredito = await generatePdfBase64(
                    <CartaDeCredito
                        form={{
                            dataFatura: { ...row },
                            dataCartaCredito: response[0],
                            dataContrato: { ...dataActiveContract, enderecoCobranca: responseEnderecoCobranca },
                        }}
                    />
                );
            }

            const { arquivoBase64: base64Kit } = await relatoriosDoKit(numFatura, `relatorio_${numFatura}`);

            const { arquivo: base64Fatura } = await boletoDaFatura({ numFatura });

            if (numeroSolicitacao) {
                setPageLoading(true);

                const getFile = async () => {
                    const file = await downloadEKit(numFatura, numeroSolicitacao);

                    if (file[0] && file[0].errorCode === 160) {
                        setTimeout(getFile, 1000);
                    } else {
                        const zip = new JSZip();

                        if (file.arquivoBase64) zip.file(`kit_${numFatura}.zip`, file.arquivoBase64, { base64: true });

                        zip.generateAsync({ type: 'base64' }).then((content) => {
                            downloadFile(`ekit-${numFatura}`, `data:application/zip;base64,${content}`);
                        });

                        setPageLoading(false);
                    }
                };

                getFile();
            } else {
                setPageLoading(false);
            }

            return {
                numFatura,
                cartaCredito: base64CartaCredito,
                kit: base64Kit,
                fatura: base64Fatura,
            };
        });

        const responses = await Promise.all(requests);

        const zip = new JSZip();

        responses.forEach(({ numFatura, cartaCredito, kit, fatura }) => {
            if (cartaCredito) zip.file(`carta_de_credito_${numFatura}.pdf`, cartaCredito, { base64: true });
            if (kit) zip.file(`kit_${numFatura}.zip`, kit, { base64: true });
            if (fatura) zip.file(`fatura_${numFatura}.pdf`, fatura, { base64: true });
        });

        zip.generateAsync({ type: 'base64' }).then((content) => {
            downloadFile('baixar_todos', `data:application/zip;base64,${content}`);
        });
    }

    useEffect(() => {
        handleSearch();
    }, []);

    const handleClickClearAll = useCallback(() => {
        setDataTable([]);

        const oldFilterValues = filterValues;

        delete oldFilterValues.competence.value;
        delete oldFilterValues.status.value;
        delete oldFilterValues.nf.value;

        setFilterValues({ ...oldFilterValues });
    }, [dataTable, filterValues]);

    return (
        <>
            <S.SectionContainer style={{ padding: '32px 32px 0' }}>
                {matchMediaQuery && (
                    <S.ToogleButton onClick={() => setShowFilters(!showFilters)}>
                        <Icon color={contrastTheme ? '#FFF333' : '#EC6E0C'} name="filter_alt" />
                        <span style={{ textDecoration: 'underline' }}>Filtrar</span>
                    </S.ToogleButton>
                )}
                <S.container>
                    <S.ContainerRadio>
                        <Radio field={filterValues.tipoSelecao} onChange={handleChangeInput} />
                    </S.ContainerRadio>

                    <S.FirstColumn showFilters={!matchMediaQuery || showFilters}>
                        <S.SelectContainer style={{ flexDirection: 'column', justifyContent: 'end' }}>
                            {filterValues.tipoSelecao.value === '2' && (
                                <Select
                                    multiple
                                    field={{
                                        ...filterValues.contractsGroup,
                                        options: adminGroups,
                                    }}
                                    onChange={handleChangeInput}
                                />
                            )}
                            {filterValues.tipoSelecao.value === '1' && (
                                <Select
                                    multiple
                                    field={{
                                        ...filterValues.contracts,
                                        options: translateContractsGroupsListToOptions(contractsGroupsList),
                                    }}
                                    onChange={handleChangeInput}
                                />
                            )}
                            <S.LinkButton
                                id="clearContracts"
                                style={{ marginTop: '-14px' }}
                                onClick={handleClearContracts}
                            >
                                <span className={enableClearSelection ? 'active' : ''}>Limpar seleção</span>
                            </S.LinkButton>
                        </S.SelectContainer>

                        <S.Row>
                            <DatepickerMui
                                field={filterValues.competence}
                                onChange={(event) => handleChangeFilter(event)}
                                openTo="month"
                                format="MM/yyyy"
                                placeholder="MM/AAAA"
                                views={['year', 'month']}
                            />

                            <Input field={filterValues.nf} onChange={(event) => handleChangeFilter(event)} />
                            <Select
                                field={{
                                    ...filterValues.status,
                                    options: STATUS_OPTIONS,
                                }}
                                onChange={(event) => handleChangeFilter(event)}
                            />
                        </S.Row>
                    </S.FirstColumn>
                </S.container>

                <S.SecondColumn showFilters={!matchMediaQuery || showFilters}>
                    <Button type="primary" disabled={loading} onClick={handleGenerateReport}>
                        Pesquisar
                    </Button>
                </S.SecondColumn>
            </S.SectionContainer>

            {dataTable.length > 0 && (
                <S.TextoUltimasFaturas>
                    {`${dataTable.length} Resultados`}
                    <S.LinkButtonRegister id="clearRegisters" onClick={handleClickClearAll}>
                        <span className="active">Limpar registros</span>
                    </S.LinkButtonRegister>
                </S.TextoUltimasFaturas>
            )}

            <S.TableContainer>
                <DataTableMui
                    data={dataTable}
                    loading={loading}
                    columns={columnsHead}
                    emptyMessage="Dados não encontrados"
                    alreadySearched={alreadySearched}
                />
            </S.TableContainer>

            {dataTable.length > 0 && !loading && (
                <S.ButtonContainer>
                    <Button type="link" onClick={handleDownloadAll}>
                        <Icon name="file_download" color={contrastTheme ? '#FFF333' : '#EC6E0C'} size={20} />
                        <span>Baixar todos</span>
                    </Button>
                </S.ButtonContainer>
            )}

            <PageLoader
                loading={pageLoading}
                message={loadingEKit ? 'Carregando... Isso pode levar alguns minutos' : false}
            />

            <Modal
                {...dataWarningModal}
                modalSize="sm"
                onCancel={() => setDataWarningModal(warningModalInitialData)}
                footer={null}
            >
                <Button type="primary" onClick={() => setDataWarningModal(warningModalInitialData)}>
                    Entendi
                </Button>
            </Modal>
        </>
    );
}

export default ConsultarFaturas;
